import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';

// Role display configuration
const ROLE_CONFIG = {
  site_supervisor: { label: 'Site Supervisor', color: 'bg-green-500' },
  site_manager: { label: 'Site Manager', color: 'bg-blue-500' },
  sid: { label: 'SID', color: 'bg-purple-500' },
  asid: { label: 'ASID', color: 'bg-indigo-500' },
  operation_head: { label: 'Operation Head', color: 'bg-orange-500' }
};

// Component to load attendance photos with authentication
const AttendancePhoto = ({ siteId, attendanceId, type = 'processed', height = 'h-20' }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        const response = await api.get(`/site-database/${siteId}/attendance/${attendanceId}/photo`, {
          params: { type },
          responseType: 'blob'
        });

        if (!mounted) return;

        const contentType = response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          if (jsonData.downloadUrl) {
            setImageSrc(jsonData.downloadUrl);
          }
          setLoading(false);
        } else {
          objectUrl = URL.createObjectURL(response.data);
          setImageSrc(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading attendance photo:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [siteId, attendanceId, type]);

  if (loading) {
    return (
      <div className={`w-full ${height} bg-gray-200 animate-pulse rounded-lg flex items-center justify-center`}>
        <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm4 8a2 2 0 100-4 2 2 0 000 4zm8 4H6l2-4 1.5 2 3-4 3.5 6z" />
        </svg>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`w-full ${height} bg-gray-100 rounded-lg flex items-center justify-center text-gray-400`}>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
          <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2" />
          <polyline points="21 15 16 10 5 21" strokeWidth="2" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt="Check-in"
      className={`w-full ${height} object-cover rounded-lg`}
    />
  );
};

export default function AttendanceMarkerPopup({ checkIn, variant = 'popup', onClose }) {
  const roleConfig = ROLE_CONFIG[checkIn.userRole] || { label: checkIn.userRole, color: 'bg-gray-500' };
  const isBottomSheet = variant === 'bottomsheet';

  return (
    <div className={isBottomSheet ? 'w-full' : 'w-full min-w-[220px] max-w-[280px]'}>
      {/* Header with name, role, and time */}
      <div className={`flex items-center gap-2 ${isBottomSheet ? 'mb-4' : 'mb-2'}`}>
        <span className={`${isBottomSheet ? 'w-4 h-4' : 'w-3 h-3'} rounded-full flex-shrink-0 ${roleConfig.color}`} />
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-gray-900 truncate leading-tight ${isBottomSheet ? 'text-base' : 'text-sm'}`}>
            {checkIn.userNickname || checkIn.userName}
          </p>
          <div className={`flex items-center gap-2 text-gray-500 ${isBottomSheet ? 'text-sm' : 'text-xs'}`}>
            <span>{roleConfig.label}</span>
            <span className="text-gray-300">•</span>
            <span className="text-green-600 font-medium">{format(new Date(checkIn.checkInTime), 'h:mm a')}</span>
          </div>
        </div>
      </div>

      {/* Site info */}
      <div className={`${isBottomSheet ? 'mb-4 p-3' : 'mb-2 p-2'} bg-gray-50 rounded-lg`}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-gray-900 truncate ${isBottomSheet ? 'text-base' : 'text-sm'}`}>
              {checkIn.siteProjectNumber}
            </p>
            {checkIn.siteClientName && (
              <p className={`text-gray-600 truncate ${isBottomSheet ? 'text-sm' : 'text-xs'}`}>
                {checkIn.siteClientName}
              </p>
            )}
          </div>
          <Link
            to={`/sites/${checkIn.siteId}`}
            onClick={() => onClose?.()}
            className={`flex-shrink-0 text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-md touch-manipulation ${
              isBottomSheet ? 'p-2' : 'p-1.5'
            }`}
            title="View Site"
          >
            <svg className={isBottomSheet ? 'w-5 h-5' : 'w-4 h-4'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </Link>
        </div>
      </div>

      {/* GPS accuracy - inline if exists */}
      {checkIn.accuracy && (
        <div className={`flex items-center gap-1.5 ${isBottomSheet ? 'mb-3 text-sm' : 'mb-2 text-xs'} text-gray-500`}>
          <svg className={`${isBottomSheet ? 'w-4 h-4' : 'w-3.5 h-3.5'} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
          <span>~{Math.round(checkIn.accuracy)}m accuracy</span>
        </div>
      )}

      {/* Notes - only show if exists */}
      {checkIn.notes && (
        <div className={`${isBottomSheet ? 'mb-3 p-2 text-sm' : 'mb-2 p-1.5 text-xs'} bg-yellow-50 rounded text-gray-600 italic ${isBottomSheet ? '' : 'truncate'}`}>
          "{checkIn.notes}"
        </div>
      )}

      {/* Photo */}
      <div className={isBottomSheet ? 'mb-4' : 'mb-2'}>
        <AttendancePhoto
          siteId={checkIn.siteId}
          attendanceId={checkIn.id}
          type="processed"
          height={isBottomSheet ? 'h-40' : 'h-20'}
        />
      </div>

      {/* Google Maps link */}
      <a
        href={`https://www.google.com/maps?q=${checkIn.latitude},${checkIn.longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center justify-center w-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg font-medium text-gray-700 transition-colors touch-manipulation ${
          isBottomSheet ? 'gap-2 py-3 px-4 text-sm' : 'gap-1.5 py-2 px-3 text-xs'
        }`}
      >
        <svg className={isBottomSheet ? 'w-4 h-4' : 'w-3.5 h-3.5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        </svg>
        Open in Google Maps
      </a>
    </div>
  );
}
